/**
 * Utilities for managing user Knowledge Graph (KG) data in Firestore
 */
import { db } from "../firebase/firebase.js";
import {
  collection,
  doc,
  getDocs,
  writeBatch,
  deleteField,
  deleteDoc,
} from "firebase/firestore";

/* ------------------------------------------------------------------ */
/*  Profile-KG (facts)                                                */
/* ------------------------------------------------------------------ */

export const fetchUserProfileKG = async (uid) => {
  const colRef = collection(db, "users", uid, "profileKG");
  const snap = await getDocs(colRef);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Upserts an array of {predicate, object, datatype, confidence, ttlDays?}
 * “id” will be auto-derived from predicate.
 */
export const upsertProfileFacts = async (uid, facts) => {
  const batch = writeBatch(db);
  facts.forEach((f) => {
    const ref = doc(db, "users", uid, "profileKG", f.predicate);
    batch.set(
      ref,
      {
        ...f,
        object: String(f.object),
        updatedAt: new Date(),
        lowConfidence: deleteField(), // 🔹 permanently remove the flag
      },
      { merge: true }
    );
  });
  await batch.commit();
};

/**
 * Updates the User Profile KG by merging an array of node objects.
 * Each node should have an 'id' field. Writes to users/{uid}/profileKG/{id}.
 */
export const updateUserProfileKG = async (uid, nodes) => {
  const batch = writeBatch(db);
  nodes.forEach((node) => {
    const { id, ...data } = node;
    const ref = doc(db, "users", uid, "profileKG", id);
    batch.set(ref, data, { merge: true });
  });
  await batch.commit();
};

/**
 * Deletes a fact document from the user's Profile KG.
 * Removes the document users/{uid}/profileKG/{id}.
 */
export const deleteProfileFact = async (uid, id) => {
  await deleteDoc(doc(db, "users", uid, "profileKG", id));
};

/**
 * Fetches the Goals Knowledge Graph nodes for a given user.
 * Returns an array of node objects stored under users/{uid}/goalsKG.
 */
export const fetchGoalsKG = async (uid) => {
  const colRef = collection(db, "users", uid, "goalsKG");
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Updates the Goals KG by merging an array of node objects.
 * Writes to users/{uid}/goalsKG/{id}.
 */
export const updateGoalsKG = async (uid, nodes) => {
  const batch = writeBatch(db);
  nodes.forEach((node) => {
    const { id, ...data } = node;
    const ref = doc(db, "users", uid, "goalsKG", id);
    batch.set(ref, data, { merge: true });
  });
  await batch.commit();
};
